import type { FC, ReactNode } from 'react'

import { cn } from '@/lib/utils'
import EditorialGrid from '@/components/generals/editorial-grid'
import DisplayHeading from '@/components/typography/display-heading'
import TextLink from '@/components/elements/text-link'

interface SectionHeadingProps {
    index?: string
    label: string
    title: ReactNode
    description?: ReactNode
    action?: {
        href: string
        label: string
    }
    id?: string
    className?: string
}

/** Section opener: ink rule, numbered mono label, Fraunces title and an optional aside. */
const SectionHeading: FC<SectionHeadingProps> = ({ index, label, title, description, action, id, className }): ReactNode => {
    return (
        <div className={cn('border-t border-foreground pt-4', className)}>
            <div className='flex items-center justify-between gap-4'>
                <p className='flex items-center gap-3 font-mono text-label uppercase text-muted-foreground'>
                    <span aria-hidden='true' className='size-1.5 bg-brand' />
                    {index && <span className='text-foreground'>{index}</span>}
                    <span>{label}</span>
                </p>

                {action && (
                    <TextLink href={action.href} arrow mono className='shrink-0'>
                        {action.label}
                    </TextLink>
                )}
            </div>

            <EditorialGrid className='mt-8 gap-y-5 md:mt-12'>
                <DisplayHeading id={id} as='h2' size='title' className='col-span-4 md:col-span-8 lg:col-span-7'>
                    {title}
                </DisplayHeading>

                {description && (
                    <p className='col-span-4 max-w-reading text-body text-muted-foreground md:col-span-4 md:self-end lg:col-span-4 lg:col-start-9'>
                        {description}
                    </p>
                )}
            </EditorialGrid>
        </div>
    )
}

export default SectionHeading
